import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Eye } from "lucide-react";
import { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { CommonPagination } from "../../components/CommonPagination";

const mockTickets = [
  {
    id: "TKT-001234",
    category: "Student",
    subject: "Scholarship Application Query",
    description: "Need help with scholarship application process...",
    submittedBy: "Ramesh Patel",
    assignedTo: "Admin Team",
    priority: "high",
    status: "open",
  },
  {
    id: "TKT-001235",
    category: "Hostel",
    subject: "Room Allocation Issue",
    description: "Student room allocation not working properly...",
    submittedBy: "Vijay Kumar",
    assignedTo: "Support Team",
    priority: "medium",
    status: "in_progress",
  },
  {
    id: "TKT-001236",
    category: "Donor",
    subject: "Tax Receipt Request",
    description: "Need tax receipt for recent donation...",
    submittedBy: "Anjali Shah",
    assignedTo: "Finance Team",
    priority: "low",
    status: "resolved",
  },
  {
    id: "TKT-001237",
    category: "Trustee",
    subject: "Access Permission",
    description: "Unable to access certain dashboard features...",
    submittedBy: "Mahesh Jain",
    assignedTo: "Tech Team",
    priority: "high",
    status: "open",
  },
  {
    id: "TKT-001238",
    category: "General",
    subject: "Website Bug Report",
    description: "Found a bug on the institutions page...",
    submittedBy: "Priya Mehta",
    assignedTo: "Dev Team",
    priority: "medium",
    status: "closed",
  },
];

type Ticket = (typeof mockTickets)[0];

export default function CRM() {
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalItems = mockTickets.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedTickets = mockTickets.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleViewTicket = useCallback((ticket: Ticket) => {
    setSelectedTicket(ticket);
  }, []);

  const handleReply = () => {
    toast.success("Reply sent successfully!");
    setSelectedTicket(null);
  };

  const tableColumns = useMemo<TableColumnDef<Ticket>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "ticketId",
        label: "Ticket ID",
        width: 120,
        renderCell: ticket => (
          <span className="font-mono font-medium">{ticket.id}</span>
        ),
      },
      {
        id: "category",
        label: "Category",
        width: 100,
        renderCell: ticket => (
          <Badge variant="outline">{ticket.category}</Badge>
        ),
      },
      {
        id: "subject",
        label: "Subject",
        width: 180,
        renderCell: ticket => (
          <span className="font-medium">{ticket.subject}</span>
        ),
      },
      {
        id: "description",
        label: "Description",
        width: 200,
        cellClassName: "max-w-xs truncate",
        renderCell: ticket => ticket.description,
      },
      {
        id: "submittedBy",
        label: "Submitted By",
        width: 140,
        renderCell: ticket => ticket.submittedBy,
      },
      {
        id: "assignedTo",
        label: "Assigned To",
        width: 140,
        renderCell: ticket => ticket.assignedTo,
      },
      {
        id: "priority",
        label: "Priority",
        width: 100,
        renderCell: ticket => (
          <Badge
            variant={
              ticket.priority === "high"
                ? "destructive"
                : ticket.priority === "medium"
                  ? "secondary"
                  : "default"
            }
          >
            {ticket.priority}
          </Badge>
        ),
      },
      {
        id: "status",
        label: "Status",
        width: 110,
        renderCell: ticket => (
          <Badge
            variant={
              ticket.status === "open"
                ? "destructive"
                : ticket.status === "in_progress"
                  ? "secondary"
                  : "default"
            }
            className={ticket.status === "resolved" ? "bg-primary" : ""}
          >
            {ticket.status.replace("_", " ")}
          </Badge>
        ),
      },
      {
        id: "actions",
        label: "Action",
        width: 80,
        alwaysVisible: true,
        renderCell: ticket => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleViewTicket(ticket)}
          >
            <Eye className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [currentPage, pageSize, handleViewTicket]
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">CRM Module</h1>
        <p className="text-muted-foreground">
          Manage support tickets and customer queries
        </p>
      </div>

      <Card>
        <CardContent className="p-3 pb-4">
          <DataTable
            title={`All Tickets (${mockTickets.length})`}
            tableId="super-admin-crm"
            columns={tableColumns}
            data={paginatedTickets}
            getRowKey={ticket => ticket.id}
            rowClassName={() => "cursor-pointer hover:bg-accent"}
          />

          {totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedTicket}
        onOpenChange={() => setSelectedTicket(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ticket Details - {selectedTicket?.id}</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <p className="text-foreground">{selectedTicket.category}</p>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Badge
                    variant={
                      selectedTicket.priority === "high"
                        ? "destructive"
                        : "secondary"
                    }
                  >
                    {selectedTicket.priority}
                  </Badge>
                </div>
                <div>
                  <Label>Submitted By</Label>
                  <p className="text-foreground">
                    {selectedTicket.submittedBy}
                  </p>
                </div>
                <div>
                  <Label>Assigned To</Label>
                  <p className="text-foreground">{selectedTicket.assignedTo}</p>
                </div>
              </div>

              <div>
                <Label>Subject</Label>
                <p className="font-medium text-foreground">
                  {selectedTicket.subject}
                </p>
              </div>

              <div>
                <Label>Description</Label>
                <p className="text-foreground">{selectedTicket.description}</p>
              </div>

              <div className="border-t pt-4">
                <Label>Thread History</Label>
                <div className="mt-2 space-y-2">
                  <div className="rounded-lg bg-accent p-3">
                    <p className="text-sm text-muted-foreground">
                      Ticket opened by {selectedTicket.submittedBy}
                    </p>
                    <p className="mt-1 text-sm">{selectedTicket.description}</p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="reply">Add Reply</Label>
                <Textarea
                  id="reply"
                  placeholder="Type your response..."
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="attachment">Attach File</Label>
                <Input id="attachment" type="file" />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSelectedTicket(null)}
                >
                  Cancel
                </Button>
                <Button onClick={handleReply}>Send Reply</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
